import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Box,
	Button,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type { MillSizeOption } from "../repositories/RollshopOperationsRepository";

const STATUS_OPTIONS: { value: string; label: string }[] = [
	{ value: "FREE", label: "Free" },
	{ value: "ASSIGNED", label: "Assigned" },
	{ value: "BROKEN", label: "Broken" },
	{ value: "SCRAPPED", label: "Scrapped" },
];

interface Props {
	receiveDateFrom: Moment;
	receiveDateTo: Moment;
	onReceiveDateFromChange: (date: Moment | null) => void;
	onReceiveDateToChange: (date: Moment | null) => void;
	search: string;
	onSearchChange: (v: string) => void;
	millSizeId: number | null;
	onMillSizeChange: (v: number | null) => void;
	statusCode: string | null;
	onStatusChange: (v: string | null) => void;
	millSizes: MillSizeOption[];
	onRefresh: () => void;
	onExportExcel: () => void;
	onAdd: () => void;
	addDisabled?: boolean;
	addDisabledReason?: string;
	isLoading: boolean;
}

export const RollsToolbar = ({
	receiveDateFrom,
	receiveDateTo,
	onReceiveDateFromChange,
	onReceiveDateToChange,
	search,
	onSearchChange,
	millSizeId,
	onMillSizeChange,
	statusCode,
	onStatusChange,
	millSizes,
	onRefresh,
	onExportExcel,
	onAdd,
	addDisabled = false,
	addDisabledReason,
	isLoading,
}: Props) => {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Receive Start"
					format="MM/DD/YYYY"
					value={receiveDateFrom}
					maxDate={receiveDateTo}
					onChange={onReceiveDateFromChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Receive End"
					format="MM/DD/YYYY"
					value={receiveDateTo}
					minDate={receiveDateFrom}
					onChange={onReceiveDateToChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ width: 220 }}>
				<TextField
					size="small"
					label="Search"
					placeholder="Name / Description / Supplier / PO"
					fullWidth
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
				/>
			</Box>
			<Box sx={{ width: 130 }}>
				<TextField
					select
					size="small"
					label="Mill Size"
					fullWidth
					value={millSizeId == null ? "" : String(millSizeId)}
					onChange={(e) =>
						onMillSizeChange(
							e.target.value === "" ? null : Number(e.target.value),
						)
					}
				>
					<MenuItem value="">All</MenuItem>
					{millSizes.map((ms) => (
						<MenuItem key={ms.Id} value={String(ms.Id)}>
							{ms.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Box sx={{ width: 160 }}>
				<TextField
					select
					size="small"
					label="Status"
					fullWidth
					value={statusCode ?? ""}
					onChange={(e) =>
						onStatusChange(e.target.value === "" ? null : e.target.value)
					}
				>
					<MenuItem value="">All</MenuItem>
					{STATUS_OPTIONS.map((s) => (
						<MenuItem key={s.value} value={s.value}>
							{s.label}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Tooltip title="Refresh">
				<span>
					<IconButton
						onClick={onRefresh}
						disabled={isLoading}
						size="small"
						sx={{
							bgcolor: "primary.main",
							color: "white",
							width: 28,
							height: 28,
							"&:hover": { bgcolor: "primary.dark" },
							"&.Mui-disabled": {
								bgcolor: "action.disabledBackground",
								color: "action.disabled",
							},
						}}
					>
						<RefreshIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</span>
			</Tooltip>

			<Tooltip title={addDisabled ? addDisabledReason : "Add new roll"}>
				<span>
					<Button
						size="small"
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={onAdd}
						disabled={addDisabled}
					>
						ADD
					</Button>
				</span>
			</Tooltip>

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
